/**
 * Type-aware cell formatting for the explorer grid.
 *
 * Reuses the form-engine's `formatValueForDisplay` (select/multi-select/boolean/
 * file labels) and adds Jalali display for date/datetime — matching how the
 * form renderer shows stored Gregorian-ISO values to the user.
 */
import { formatValueForDisplay } from '@/lib/form-engine'
import {
  parseStored,
  displayCalendarFor,
  displayLocaleFor,
} from '@/lib/form-engine/renderer/inputs/jalali-date-utils'
import {
  RECORD_STATUS_LABELS,
  SUBMISSION_STATUS_LABELS,
} from '../types/data-view-spec'
import type { CatalogField } from './column-catalog'

const EMPTY = '—'

const persianCal = displayCalendarFor('jalali')
const persianLoc = displayLocaleFor('jalali')

/** Stored Gregorian ISO → Jalali display string. Empty/invalid → em dash. */
export function formatJalali(value: unknown, withTime: boolean): string {
  if (typeof value !== 'string' || !value) return EMPTY
  const obj = parseStored(value, withTime, persianCal, persianLoc)
  if (!obj) return EMPTY
  return obj.format(withTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD')
}

/** Format a single field value for display, given its catalogue entry. */
export function formatCellValue(field: CatalogField | undefined, val: unknown): string {
  if (val === null || val === undefined || val === '') return EMPTY
  if (!field) return String(val)

  switch (field.fieldType) {
    case 'date':
      return formatJalali(val, false)
    case 'datetime':
      return formatJalali(val, true)
    case 'time':
      return String(val)
    default:
      // single_select / multi_select / boolean / number / text / file → engine helper
      return formatValueForDisplay(field.definition, val)
  }
}

/** Format a record-metadata value (display_id, statuses, timestamps, counts). */
export function formatMetaValue(key: string, val: unknown): string {
  if (val === null || val === undefined || val === '') return EMPTY
  switch (key) {
    case 'record_status':
      return RECORD_STATUS_LABELS[String(val)] ?? String(val)
    case 'submission_status':
      return SUBMISSION_STATUS_LABELS[String(val)] ?? String(val)
    case 'record_created_at':
    case 'record_updated_at':
      return formatJalali(String(val).slice(0, 16).replace(' ', 'T'), true)
    default:
      return String(val)
  }
}
